/**
 * Tenant → Product → Services drill-down (deepest level).
 *
 * Shows one row per product_instance the tenant has emitted events for
 * within the chosen product, with cost, event count, and duration stats.
 */
import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import CostWindowSelector from '@/components/tenants/CostWindowSelector';
import { useServiceCosts } from '@/hooks/useTenantCosts';
import type { CostPeriod, CostWindowParams } from '@/api/tenants';

function formatUsd(s: string): string {
  const n = Number(s);
  if (Number.isNaN(n)) return '$0.00';
  if (n >= 100) return `$${n.toFixed(2)}`;
  if (n >= 1) return `$${n.toFixed(3)}`;
  return `$${n.toFixed(4)}`;
}

function formatTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function formatMs(v: number | null): string {
  if (v == null) return '—';
  return `${v.toFixed(1)} ms`;
}

function paramsToWindow(p: URLSearchParams): CostWindowParams {
  const period = (p.get('period') as CostPeriod | null) ?? '30d';
  const start = p.get('start') ?? undefined;
  const end = p.get('end') ?? undefined;
  return { period, start, end };
}

function windowToParams(w: CostWindowParams): string {
  const p = new URLSearchParams();
  if (w.period) p.set('period', w.period);
  if (w.start) p.set('start', w.start);
  if (w.end) p.set('end', w.end);
  return p.toString();
}

export default function TenantProductServices() {
  const { tenantId = '', product = '' } = useParams<{ tenantId: string; product: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [window, setWindowState] = useState<CostWindowParams>(() => paramsToWindow(searchParams));

  function setWindow(next: CostWindowParams) {
    setWindowState(next);
    setSearchParams(new URLSearchParams(windowToParams(next)));
  }

  const { data, isLoading, error } = useServiceCosts(tenantId, product, window);

  const totalCost = useMemo(
    () => (data?.rows ?? []).reduce((sum, r) => sum + Number(r.total_cost_usd), 0),
    [data],
  );

  const drillSuffix = `?${windowToParams(window)}`;

  return (
    <div className="space-y-6 animate-fade-in">
      <nav className="flex flex-wrap items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
        <Link
          to={`/tenants${drillSuffix}`}
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200 transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          Tenants
        </Link>
        <span className="text-slate-300 dark:text-slate-600">/</span>
        <Link
          to={`/tenants/${encodeURIComponent(tenantId)}${drillSuffix}`}
          className="font-mono rounded-md px-2 py-1 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200 transition-colors"
        >
          {tenantId}
        </Link>
        <span className="text-slate-300 dark:text-slate-600">/</span>
        <span className="font-mono text-slate-700 dark:text-slate-200">{product}</span>
      </nav>

      <header>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
          {product} services
        </h1>
        <p className="mt-1 font-mono text-sm text-slate-500 dark:text-slate-400">{tenantId}</p>
      </header>

      <CostWindowSelector value={window} onChange={setWindow} />

      {error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/30 dark:text-rose-200">
          Failed to load services: {(error as Error).message}
        </div>
      )}

      <div className="rounded-2xl border border-slate-200/60 bg-white shadow-card overflow-hidden dark:border-slate-800/80 dark:bg-slate-900">
        <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
          <thead className="bg-surface-50 dark:bg-slate-800/50">
            <tr>
              <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                Service / Instance
              </th>
              <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                Source
              </th>
              <th className="px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                Cost
              </th>
              <th className="px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                Events
              </th>
              <th className="px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                Avg ms
              </th>
              <th className="px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                p95 ms
              </th>
              <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                Last Activity
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white dark:divide-slate-800 dark:bg-slate-900">
            {isLoading && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                  Loading…
                </td>
              </tr>
            )}
            {!isLoading && (data?.rows.length ?? 0) === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                  No services in this window.
                </td>
              </tr>
            )}
            {data?.rows.map((row) => (
              <tr
                key={row.product_instance}
                className="hover:bg-surface-50 dark:hover:bg-slate-800/30 transition-colors"
              >
                <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-slate-900 dark:text-slate-100">
                  {row.product_instance}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-500 dark:text-slate-400">
                  {row.service_name ?? '—'}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm tabular-nums text-slate-900 dark:text-slate-100">
                  {formatUsd(row.total_cost_usd)}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums text-sm text-slate-600 dark:text-slate-300">
                  {row.event_count.toLocaleString()}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums text-sm text-slate-600 dark:text-slate-300">
                  {formatMs(row.avg_duration_ms)}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums text-sm text-slate-600 dark:text-slate-300">
                  {formatMs(row.p95_duration_ms)}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-500 dark:text-slate-400">
                  {formatTime(row.last_event_at)}
                </td>
              </tr>
            ))}
          </tbody>
          {(data?.rows.length ?? 0) > 0 && (
            <tfoot className="bg-surface-50 dark:bg-slate-800/50">
              <tr>
                <td colSpan={2} className="px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  Total
                </td>
                <td className="whitespace-nowrap px-4 py-2 text-right font-mono text-sm tabular-nums text-slate-900 dark:text-slate-100">
                  ${totalCost.toFixed(2)}
                </td>
                <td colSpan={4}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
